import React from 'react';
import { useLoaderData } from 'react-router-dom';
import { VaBreadcrumbs } from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import { useFeatureToggle } from '~/platform/utilities/feature-toggles/useFeatureToggle';
import ClaimantDetailsWrapper from '../components/ClaimantDetailsWrapper';
import api from '../utilities/api';
import ClaimantNotFound from '../components/ClaimantNotFound';
import { claimantSubmitFormsBC } from '../utilities/poaRequests';
import SubmitFormsAndFilesContent from '../components/SubmitFormsAndFilesContent';
import { SUBMISSIONS_PATH } from '../utilities/constants';

const ClaimantSubmitFormsPage = () => {
  const { TOGGLE_NAMES, useToggleValue } = useFeatureToggle();
  const isClaimantDetailsUpdateEnabled = useToggleValue(
    TOGGLE_NAMES.accreditedRepresentativePortalCdUpdateMain,
  );

  const {
    authorized = true,
    firstName = null,
    lastName = null,
    notRepresented = false,
    claimantId = null,
  } = useLoaderData() || {};

  if (!authorized && notRepresented) {
    return <ClaimantNotFound />;
  }

  document.title = `Claimant ${lastName}, ${firstName} submit forms and files | Accredited Representative Portal | Veterans Affairs`;

  return (
    <section className="vads-u-width--full claimant-details-submission-history">
      <div className="claimant-details-submission-history__breadcrumbs">
        <VaBreadcrumbs
          breadcrumbList={claimantSubmitFormsBC}
          homeVeteransAffairs={false}
          wrapping
        />
      </div>

      <ClaimantDetailsWrapper firstName={firstName} lastName={lastName}>
        <h2
          data-testid="submission-history-page-heading"
          className="vads-u-margin-y--0"
        >
          Submit forms and files
        </h2>
        <p className="va-introtext vads-u-margin-y--0 vads-u-margin-bottom--2">
          Submit online forms, or upload forms or documents.
        </p>
        {isClaimantDetailsUpdateEnabled ? (
          <SubmitFormsAndFilesContent claimantId={claimantId} />
        ) : (
          <>
            <p>This page isn’t available yet.</p>
            <va-link-action
              href={SUBMISSIONS_PATH}
              text="Submit forms"
              type="primary"
            />
          </>
        )}
      </ClaimantDetailsWrapper>
    </section>
  );
};

ClaimantSubmitFormsPage.loader = async ({ params }) => {
  const claimantId = params?.claimantId;
  if (!claimantId) throw new Response('Not Found', { status: 404 });

  try {
    const overviewRes = await api.getClaimantOverview(claimantId);
    if (overviewRes.status === 401) throw overviewRes;
    if (!overviewRes.ok) throw overviewRes;

    const overviewJson = await overviewRes.json();
    const overviewRaw = overviewJson?.data || overviewJson;
    const { first_name: firstName, last_name: lastName } = overviewRaw;

    return { authorized: true, firstName, lastName, claimantId };
  } catch (err) {
    if (err instanceof Response && err.status === 403) {
      return {
        authorized: false,
        claimant: null,
        claimantId,
        notRepresented: true,
      };
    }

    throw err;
  }
};

export default ClaimantSubmitFormsPage;
